'use client'

import { useSearchParams } from 'next/navigation'
import { useCallback } from 'react'

/**
 * Custom hook to manage search parameter state in the URL.
 *
 * The first item in the returned array is the current value of the search parameter associated with the given key.
 * The value will be `null` if the search parameter is not present in the URL.
 *
 * The second item in the returned array is a function that updates the search parameter in the URL to the specified value.
 * When called, the function appends or updates the search parameter in the URL and retains existing search parameters.
 */
const useSearchParamState = <TValue extends string>(key: string, defaultValue?: TValue) => {
  const searchParams = useSearchParams()
  const value = searchParams.has(key) ? searchParams.get(key) as TValue : defaultValue

  /**
   * A callback function that updates a URL parameter with the next value
   * and pushes the updated URL to the router while preventing scroll.
   *
   * @param {string} nextValue - The value to set in the URL parameter.
   */
  const setValue = useCallback((nextValue?: TValue, options?: SetValueOptions) => {
    const currentParams = new URLSearchParams(window.location.search)

    if (options?.deleteOtherParams) {
      currentParams.forEach((_, currentKey) => {
        if (currentKey !== key) {
          currentParams.delete(currentKey)
        }
      })
    }

    if (nextValue) {
      currentParams.set(key, String(nextValue))
    }
    else {
      currentParams.delete(key)
    }

    window.history.pushState(null, '', `?${currentParams.toString()}`)
  }, [key])

  return [value, setValue] as const
}

export interface SetValueOptions {
  /**
  * When true, removes all other search parameters except the one being set.
  */
  deleteOtherParams?: boolean
}

export default useSearchParamState
